import { useTranslation } from "react-i18next";
import { CircleCheck, TriangleAlert } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import {
  formatBidRequestDate,
  formatMoney,
  getPaymentMethodName,
  isSettled,
} from "../../../BidRequests/utils";
import type { Currency, Payment, Settlement } from "../../../BidRequests/types";

interface PaymentMethodLike {
  id: number | string;
  name: string;
  translations?: Record<string, string>;
}

interface PaymentsTabProps {
  settlement: Settlement;
  currency: Currency | null;
  aggregatorName: string;
  paymentMethods: PaymentMethodLike[];
  language: string;
}

export const PaymentsTab = ({
  settlement,
  currency,
  aggregatorName,
  paymentMethods,
  language,
}: PaymentsTabProps) => {
  const { t } = useTranslation();
  const settled = isSettled(settlement);

  const renderPayment = (payment: Payment) => {
    const methodName = getPaymentMethodName(payment.payment_method, paymentMethods, language);
    return (
      <Card key={payment.uuid} className="gap-0 py-0">
        <CardContent className="flex flex-col gap-1 p-5">
          <p className="text-muted-foreground text-xs">
            {methodName
              ? t("bidRequests.detail.payments.methodLabel", { method: methodName })
              : t("bidRequests.detail.payments.paymentLabel")}
          </p>
          <p className="font-semibold text-lg">{formatMoney(currency, payment.amount)}</p>
          <p className="text-muted-foreground text-sm">
            {t("bidRequests.detail.payments.paidTo", {
              date: formatBidRequestDate(payment.paid_at),
              aggregator: aggregatorName,
            })}
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {settled ? (
        <div className="flex items-start gap-3 rounded-lg border border-success-300 bg-success-100 p-4">
          <CircleCheck className="mt-0.5 size-4 text-success-700" />
          <div className="flex flex-col gap-0.5">
            <p className="font-semibold text-sm">{t("bidRequests.detail.payments.paidInFull")}</p>
            <p className="text-muted-foreground text-sm">
              {formatMoney(currency, settlement.agreed_total)}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-warning-300 bg-warning-100 p-4">
          <TriangleAlert className="mt-0.5 size-4 text-warning-700" />
          <div className="flex flex-col gap-0.5">
            <p className="font-semibold text-sm">
              {t("bidRequests.detail.payments.outstanding", {
                amount: formatMoney(currency, settlement.outstanding),
              })}
            </p>
            <p className="text-muted-foreground text-sm">
              {t("bidRequests.detail.payments.outstandingOfTotal", {
                total: formatMoney(currency, settlement.agreed_total),
              })}
            </p>
          </div>
        </div>
      )}

      {settlement.payments.length === 0 ? (
        <p className="rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground text-sm">
          {t("bidRequests.detail.payments.empty")}
        </p>
      ) : (
        settlement.payments.map(renderPayment)
      )}
    </div>
  );
};
